// src/pages/SignIns.jsx
import { useEffect, useState, useRef, useCallback } from 'react';
import { useApp } from '../AppContext';
import { api } from '../api';
import { Modal, Btn } from '../components/UI';

const CARD_COLORS = {
  family: '#1a56a0', junior: '#b45309', single: '#166534', couple: '#6b21a8',
};

function looksLikeCompleteMemberPayload(v) {
  v = v.trim();
  if (!v) return false;
  if (v[0] === '{' && v[v.length - 1] === '}') return true; // JSON payload
  // Pipe-delimited: NUM|LASTNAME|PARENTS|CHILDREN|TYPE|TOTAL[|CAREGIVER].
  // Caregiver is optional, so a 6-part string is valid on its own — but
  // that same part-count also shows up mid-typing, right after TYPE and
  // before TOTAL has been entered (the trailing part is just empty then).
  // Require TOTAL itself to be filled in with a number so we don't
  // mistake "still typing the total" for "done".
  const parts = v.split('|');
  return parts.length >= 6 && /^\d+$/.test(parts[5].trim());
}

function StatBox({ label, value, color }) {
  return (
    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', padding: '12px 14px', flex: 1, textAlign: 'center' }}>
      <div style={{ fontSize: 22, fontWeight: 'bold', color: color || 'var(--fg)' }}>{value}</div>
      <div style={{ fontSize: 11, color: 'var(--fg3)', marginTop: 2 }}>{label}</div>
    </div>
  );
}

export default function SignIns() {
  const { site, user } = useApp();
  const pricing = site?.pricing || {};
  const features = site?.features || {};

  const [summary, setSummary]         = useState(null);
  const [entries, setEntries]         = useState([]);
  const [member, setMember]           = useState(null);
  const [memberCount, setMemberCount] = useState(1);
  const [guestSwimmers,    setGuestSwimmers]    = useState(0);
  const [guestNonSwimmers, setGuestNonSwimmers] = useState(0);
  const [guestUnder2,      setGuestUnder2]      = useState(0);
  const [payMethod, setPayMethod]     = useState('cash');
  const [guardName, setGuardName]     = useState('');
  const [caregiver, setCaregiver]     = useState(false);
  const [flash, setFlash]             = useState(null);
  const [submitting, setSubmitting]   = useState(false);
  const [lookupQ, setLookupQ]         = useState('');
  const [lookupResults, setLookupResults] = useState([]);
  const [showLookup, setShowLookup]   = useState(false);
  const [partyWarning, setPartyWarning] = useState(false);
  const [partyAck, setPartyAck]       = useState(false);
  const [scannedGuests, setScannedGuests] = useState([]);
  const [licenseFlash, setLicenseFlash]   = useState(null);
  const [lastSigninId, setLastSigninId]   = useState(null);
  const [detailEntry, setDetailEntry]         = useState(null);
  const [detailMembers, setDetailMembers]     = useState(1);
  const [detailSwimmers, setDetailSwimmers]   = useState(0);
  const [detailNonSwimmers, setDetailNonSwimmers] = useState(0);
  const [detailUnder2, setDetailUnder2]       = useState(0);
  const [detailSaving, setDetailSaving]       = useState(false);
  const [detailScannedGuests, setDetailScannedGuests] = useState([]);
  const [detailLicenseFlash, setDetailLicenseFlash]   = useState(null);
  const [logSearch, setLogSearch]     = useState('');
  const [onsiteOnly, setOnsiteOnly]   = useState(false);
  const licenseTimer = useRef(null);
  const qrTimer = useRef(null);
  const detailLicenseTimer = useRef(null);
  const qrRef = useRef(null);
  const lookupTimer = useRef(null);

  const guestCount = guestSwimmers + guestNonSwimmers + guestUnder2;
  const rateSwim = payMethod === 'card' ? (pricing.party_swim_card || 5.25) : (pricing.party_swim_cash || 5.00);
  const rateNon  = payMethod === 'card' ? (pricing.party_nonswim_card || 2.12) : (pricing.party_nonswim_cash || 2.00);
  const total    = (guestSwimmers * rateSwim) + (guestNonSwimmers * rateNon);
  const isJunior = member?.membership_type?.toLowerCase() === 'junior';

  const refresh = useCallback(() => {
    api.todaySummary().then(r => r?.ok && setSummary(r.data));
    api.signinsToday().then(r => r?.ok && setEntries(r.data.entries || []));
  }, []);

  useEffect(() => { refresh(); qrRef.current?.focus(); }, [refresh]);

  // QR scanner
  const handleLicense = useCallback(async (raw) => {
    if (!raw.trim() || raw.length < 50) return;
    clearTimeout(licenseTimer.current);
    const res = await api.scanLicense(raw.trim());
    if (res?.ok) {
      const g = res.data.guest;
      if (scannedGuests.find(s => s.id === g.id)) {
        setLicenseFlash({ type: 'error', msg: `${g.first_name} ${g.last_name} already scanned.` });
        return;
      }
      setScannedGuests(prev => [...prev, g]);
      setLicenseFlash({ type: 'success', msg: `${g.first_name} ${g.last_name} scanned.` });
    } else if (res?.status === 403) {
      setLicenseFlash({ type: 'error', msg: res.data?.error || 'Guest is flagged.' });
    } else {
      setLicenseFlash({ type: 'error', msg: res?.data?.error || 'Could not read license.' });
    }
    setTimeout(() => setLicenseFlash(null), 4000);
  }, [scannedGuests]);

  const handleQR = useCallback(async (raw) => {
    if (!raw.trim()) return;
    const res = await api.scanQR(raw.trim());
    if (res?.ok) {
      loadMember(res.data.member);
    } else {
      showFlash(res?.data?.error || 'Member not found', 'error');
    }
  }, []);

  function loadMember(m) {
    const status = m.status || (m.resigned ? 'resigned' : 'active');
    if (status === 'resigned') {
      showFlash(`This membership has been resigned and is no longer active.`, 'error');
      return;
    }
    if (status === 'inactive') {
      showFlash(`Membership #${m.membership_number} — ${m.lastname} is currently inactive. Dues must be confirmed by a manager before entry.`, 'error');
      return;
    }
    setMember(m);
    setMemberCount(m.total_people || 1);
    setGuestSwimmers(0);
    setGuestNonSwimmers(0);
    setGuestUnder2(0);
    setPartyWarning(false);
    setPartyAck(false);
    setFlash(null);
  }

  // Member lookup
  useEffect(() => {
    if (!lookupQ.trim()) { setLookupResults([]); setShowLookup(false); return; }
    clearTimeout(lookupTimer.current);
    lookupTimer.current = setTimeout(async () => {
      const res = await api.memberLookup(lookupQ);
      if (res?.ok) { setLookupResults(res.data.members || []); setShowLookup(true); }
    }, 300);
  }, [lookupQ]);

  function showFlash(msg, type = 'info') {
    setFlash({ msg, type });
    setTimeout(() => setFlash(null), 4000);
  }

  async function handleSubmit() {
    if (!member) { showFlash('Scan or select a member first', 'error'); return; }
    if (!guardName.trim()) { showFlash('Enter your name as the guard', 'error'); return; }
    if (guestCount >= 6 && !partyAck) { setPartyWarning(true); return; }

    setSubmitting(true);
    const res = await api.submitSignin({
      membership_number: member.membership_number,
      lastname:          member.lastname,
      membership_type:   member.membership_type,
      member_people:     memberCount,
      guest_count:       guestCount,
      guest_swimmers:     guestSwimmers,
      guest_non_swimmers: guestNonSwimmers,
      guest_under_2:      guestUnder2,
      payment_method:    payMethod,
      guard_name:        guardName,
      caregiver_present: caregiver,
      party_override:    partyAck,
    });
    setSubmitting(false);

    if (res?.ok) {
      showFlash(`✓ ${member.lastname} signed in — $${(res.data.total_charge || 0).toFixed(2)}`, 'success');
      setSummary(res.data.summary);
      const newSigninId = res.data.signin_id || null;
      setLastSigninId(newSigninId);

      // Record any guest ID scans against this signin
      if (features.guest_id && newSigninId && scannedGuests.length > 0) {
        for (const g of scannedGuests) {
          await api.recordGuestVisit(newSigninId, g.id, member.membership_number);
        }
      }

      setScannedGuests([]);
      setMember(null); setMemberCount(1); setGuestSwimmers(0); setGuestNonSwimmers(0); setGuestUnder2(0);
      setPartyWarning(false); setPartyAck(false);
      await api.signinsToday().then(r => r?.ok && setEntries(r.data.entries || []));
      qrRef.current?.focus();
    } else {
      showFlash(res?.data?.error || 'Error submitting', 'error');
    }
  }

  async function handleUndo() {
    if (!window.confirm('Undo the last sign-in?')) return;
    const res = await api.undoSignin();
    if (res?.ok) { showFlash('Last sign-in undone', 'success'); refresh(); }
    else showFlash('Nothing to undo', 'error');
  }

  async function openDetail(e) {
    setDetailEntry(e);
    setDetailMembers(e.member_people || 1);
    setDetailSwimmers(e.guest_swimmers || 0);
    setDetailNonSwimmers(e.guest_non_swimmers || 0);
    setDetailUnder2(e.guest_under_2 || 0);
    setDetailLicenseFlash(null);

    if (features.guest_id && e.id) {
      const res = await api.signinGuests(e.id);
      setDetailScannedGuests(res?.ok ? (res.data.guests || []) : []);
    } else {
      setDetailScannedGuests([]);
    }
  }

  function closeDetail() {
    setDetailEntry(null);
    setDetailSaving(false);
  }

  // Attaches a scanned guest directly to the already-existing sign-in being
  // viewed in the modal — unlike handleLicense, which only stages guests for
  // the *next* submitted transaction.
  const handleDetailLicense = useCallback(async (raw) => {
    if (!raw.trim() || raw.length < 50 || !detailEntry?.id) return;
    clearTimeout(detailLicenseTimer.current);
    const res = await api.scanLicense(raw.trim());
    if (res?.ok) {
      const g = res.data.guest;
      if (detailScannedGuests.find(s => s.id === g.id)) {
        setDetailLicenseFlash({ type: 'error', msg: `${g.first_name} ${g.last_name} already scanned.` });
      } else {
        await api.recordGuestVisit(detailEntry.id, g.id, detailEntry.membership_number);
        setDetailScannedGuests(prev => [...prev, g]);
        setDetailLicenseFlash({ type: 'success', msg: `${g.first_name} ${g.last_name} added to this sign-in.` });
      }
    } else if (res?.status === 403) {
      setDetailLicenseFlash({ type: 'error', msg: res.data?.error || 'Guest is flagged.' });
    } else {
      setDetailLicenseFlash({ type: 'error', msg: res?.data?.error || 'Could not read license.' });
    }
    setTimeout(() => setDetailLicenseFlash(null), 4000);
  }, [detailEntry, detailScannedGuests]);

  async function saveDetailMembers() {
    if (!detailEntry?.id) return;
    setDetailSaving(true);
    const res = await api.updateMembers(detailEntry.id, detailMembers);
    setDetailSaving(false);
    if (res?.ok) {
      setEntries(prev => prev.map(e =>
        e.id === detailEntry.id ? { ...e, member_people: detailMembers } : e
      ));
      setDetailEntry(prev => ({ ...prev, member_people: detailMembers }));
      showFlash('Member count updated', 'success');
    } else {
      showFlash(res?.data?.error || 'Error saving', 'error');
    }
  }

  async function saveDetailGuests() {
    if (!detailEntry?.id) return;
    setDetailSaving(true);
    const res = await api.updateGuests(detailEntry.id, detailSwimmers, detailNonSwimmers, detailUnder2);
    setDetailSaving(false);
    if (res?.ok) {
      const d = res.data;
      setEntries(prev => prev.map(e =>
        e.id === detailEntry.id ? {
          ...e,
          guest_swimmers:     detailSwimmers,
          guest_non_swimmers: detailNonSwimmers,
          guest_under_2:      detailUnder2,
          guest_count:        d.guest_count,
          total_charge:       d.total_charge,
        } : e
      ));
      setDetailEntry(prev => ({
        ...prev,
        guest_swimmers:     detailSwimmers,
        guest_non_swimmers: detailNonSwimmers,
        guest_under_2:      detailUnder2,
        guest_count:        d.guest_count,
        total_charge:       d.total_charge,
      }));
      if (d.summary) setSummary(d.summary);
      showFlash('Guest count updated', 'success');
    } else {
      showFlash(res?.data?.error || 'Error saving', 'error');
    }
  }

  async function saveDetailDeparture() {
    if (!detailEntry?.id) return;
    setDetailSaving(true);
    const res = await api.departure(detailEntry.id);
    setDetailSaving(false);
    if (res?.ok) {
      const dep = res.data?.departure_time;
      setEntries(prev => prev.map(e =>
        e.id === detailEntry.id ? { ...e, departure_time: dep } : e
      ));
      setDetailEntry(prev => ({ ...prev, departure_time: dep }));
      showFlash(dep ? 'Departure recorded' : 'Departure cleared', 'success');
    } else {
      showFlash(res?.data?.error || 'Error saving', 'error');
    }
  }

  const inp = (style) => ({
    width: '100%', padding: '8px 10px', border: '1px solid var(--input-border)',
    background: 'var(--input-bg)', color: 'var(--fg)', fontSize: 14, ...style,
  });

  const payBtn = (m) => ({
    flex: 1, padding: '9px 8px', border: '1px solid var(--border)',
    cursor: 'pointer', fontWeight: 'bold', fontSize: 12,
    background: payMethod === m ? 'var(--accent)' : 'var(--card-bg)',
    color: payMethod === m ? '#fff' : 'var(--fg2)',
  });

  const typeColor = (t) => CARD_COLORS[(t || '').toLowerCase()] || 'var(--accent)';

  const filteredEntries = entries.filter(e => {
    const q = logSearch.trim().toLowerCase();
    const matchesText = !q
      || e.lastname.toLowerCase().includes(q)
      || String(e.membership_number).toLowerCase().includes(q);
    const matchesOnsite = !onsiteOnly || !e.departure_time;
    return matchesText && matchesOnsite;
  });

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 16 }}>
      {flash && (
        <div className={`flash ${flash.type}`} style={{ marginBottom: 12 }}>
          {flash.msg}
        </div>
      )}

      {summary && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <StatBox label="Signed In"  value={summary.total_people} />
          <StatBox label="Members"    value={summary.total_members} />
          <StatBox label="Guests"     value={summary.total_guests} color="var(--warning)" />
          {features.revenue && (
            <StatBox label="Revenue" value={`$${(summary.total_revenue||0).toFixed(2)}`} color="var(--success)" />
          )}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

        {/* LEFT — sign-in form */}
        <div>
          <div className="card" style={{ padding: 14, marginBottom: 12 }}>
            <div style={{ fontSize: 11, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--fg3)', marginBottom: 6 }}>Scan QR Code</div>
            <input
              ref={qrRef}
              type="text"
              placeholder="Scan QR code here..."
              autoComplete="off"
              style={inp()}
              onKeyDown={e => {
                if (e.key === 'Enter' && e.target.value.length > 3) {
                  e.preventDefault();
                  handleQR(e.target.value);
                  e.target.value = '';
                }
              }}
              onChange={e => {
                const v = e.target.value;
                clearTimeout(qrTimer.current);
                if (looksLikeCompleteMemberPayload(v)) {
                  qrTimer.current = setTimeout(() => { handleQR(v); e.target.value = ''; }, 600);
                }
              }}
            />
          </div>

          <div className="card" style={{ padding: 14, marginBottom: 12, position: 'relative' }}>
            <div style={{ fontSize: 11, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--fg3)', marginBottom: 6 }}>Manual Lookup</div>
            <input
              type="text"
              placeholder="Last name or membership #"
              value={lookupQ}
              onChange={e => setLookupQ(e.target.value)}
              onBlur={() => setTimeout(() => setShowLookup(false), 200)}
              style={inp()}
            />
            {showLookup && lookupResults.length > 0 && (
              <div style={{
                position: 'absolute', left: 0, right: 0, top: '100%', zIndex: 50,
                background: 'var(--card-bg)', border: '1px solid var(--accent)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)', maxHeight: 220, overflowY: 'auto',
              }}>
                {lookupResults.map((m, i) => (
                  <div key={i}
                    onMouseDown={() => { loadMember(m); setLookupQ(''); setShowLookup(false); }}
                    style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border-light)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg2)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <div style={{ fontWeight: 'bold', color: 'var(--fg)' }}>{m.lastname}</div>
                    <div style={{ fontSize: 11, color: 'var(--fg3)' }}>
                      #{m.membership_number} · {m.membership_type} · <span style={{ color: m.status === 'active' ? 'var(--success)' : '#c00' }}>{m.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {member && (
            <div style={{
              background: 'var(--card-bg)', borderLeft: `4px solid ${typeColor(member.membership_type)}`,
              border: '1px solid var(--border)', padding: 12, marginBottom: 12,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 'bold' }}>{member.lastname}</div>
                  <div style={{ fontSize: 12, color: 'var(--fg3)', marginTop: 2 }}>
                    #{member.membership_number} · {member.membership_type}
                  </div>
                </div>
                <span style={{ fontSize: 10, padding: '2px 7px', fontWeight: 'bold', background: typeColor(member.membership_type), color: '#fff' }}>
                  {member.membership_type}
                </span>
              </div>
              {member.parents   && <div style={{ fontSize: 12, color: 'var(--fg2)', marginTop: 6 }}>Members: {member.parents}</div>}
              {member.children  && <div style={{ fontSize: 12, color: 'var(--fg2)' }}>Children: {member.children}</div>}
              {member.caregiver && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 6, cursor: 'pointer' }}>
                  <input type="checkbox" checked={caregiver} onChange={e => setCaregiver(e.target.checked)} />
                  Caregiver present ({member.caregiver})
                </label>
              )}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div>
              <label style={{ fontSize: 11, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--fg2)', display: 'block', marginBottom: 4 }}>Members Today</label>
              <input type="number" min={1} value={memberCount} onChange={e => setMemberCount(Math.max(1, parseInt(e.target.value) || 1))} style={inp()} />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--fg2)', display: 'block', marginBottom: 4 }}>Swimmers</label>
              <input
                type="number" min={0} value={guestSwimmers} disabled={isJunior}
                onChange={e => { const v = Math.max(0, parseInt(e.target.value) || 0); setGuestSwimmers(v); const t = v + guestNonSwimmers + guestUnder2; setPartyWarning(t >= 6); setPartyAck(false); }}
                style={inp({ background: isJunior ? 'var(--bg3)' : undefined })}
              />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--fg2)', display: 'block', marginBottom: 4 }}>Non-Swim</label>
              <input
                type="number" min={0} value={guestNonSwimmers} disabled={isJunior}
                onChange={e => { const v = Math.max(0, parseInt(e.target.value) || 0); setGuestNonSwimmers(v); const t = guestSwimmers + v + guestUnder2; setPartyWarning(t >= 6); setPartyAck(false); }}
                style={inp({ background: isJunior ? 'var(--bg3)' : undefined })}
              />
            </div>
            <div>
              <label style={{ fontSize: 11, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--fg2)', display: 'block', marginBottom: 4 }}>Under 2 (free)</label>
              <input
                type="number" min={0} value={guestUnder2} disabled={isJunior}
                onChange={e => { const v = Math.max(0, parseInt(e.target.value) || 0); setGuestUnder2(v); const t = guestSwimmers + guestNonSwimmers + v; setPartyWarning(t >= 6); setPartyAck(false); }}
                style={inp({ background: isJunior ? 'var(--bg3)' : undefined })}
              />
            </div>
          </div>
          {isJunior && <div style={{ fontSize: 11, color: '#c00', marginBottom: 8 }}>Junior memberships cannot bring guests</div>}

          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <button style={payBtn('cash')} onClick={() => setPayMethod('cash')}>
              Cash — Swim ${(pricing.party_swim_cash || 5.00).toFixed(2)} · Non ${(pricing.party_nonswim_cash || 2.00).toFixed(2)}
            </button>
            <button style={payBtn('card')} onClick={() => setPayMethod('card')}>
              Card — Swim ${(pricing.party_swim_card || 5.25).toFixed(2)} · Non ${(pricing.party_nonswim_card || 2.12).toFixed(2)}
            </button>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--fg2)', display: 'block', marginBottom: 4 }}>Guard Name</label>
            <input type="text" value={guardName} onChange={e => setGuardName(e.target.value)} placeholder="Your name" style={inp()} />
          </div>

          {partyWarning && (
            <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', padding: '10px 12px', marginBottom: 10, fontSize: 13 }}>
              <strong>6+ guests — party booking may be required.</strong>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6, cursor: 'pointer', fontSize: 12 }}>
                <input type="checkbox" checked={partyAck} onChange={e => setPartyAck(e.target.checked)} />
                I acknowledge — no party booking needed for this group
              </label>
            </div>
          )}

          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 11, color: 'var(--fg3)' }}>Total Due (guests only)</div>
              {guestSwimmers    > 0 && <div style={{ fontSize: 11, color: 'var(--fg3)' }}>{guestSwimmers} swimmer{guestSwimmers !== 1 ? 's' : ''} × ${rateSwim.toFixed(2)}</div>}
              {guestNonSwimmers > 0 && <div style={{ fontSize: 11, color: 'var(--fg3)' }}>{guestNonSwimmers} non-swim × ${rateNon.toFixed(2)}</div>}
              {guestUnder2      > 0 && <div style={{ fontSize: 11, color: 'var(--fg3)' }}>{guestUnder2} under 2 — free</div>}
            </div>
            <div style={{ fontSize: 24, fontWeight: 'bold' }}>${total.toFixed(2)}</div>
          </div>

          <button
            className="btn btn-primary"
            style={{ width: '100%', padding: 12, fontSize: 15, marginBottom: 8 }}
            onClick={handleSubmit}
            disabled={submitting || !member || (partyWarning && !partyAck)}
          >
            {submitting ? 'Submitting...' : 'Complete Sign-In'}
          </button>
          <button className="btn btn-outline" style={{ width: '100%', fontSize: 12 }} onClick={handleUndo}>
            Undo Last Sign-In
          </button>
        </div>

        {/* RIGHT — today log + guest scanning */}
        <div>
          <div className="card">
            <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', fontWeight: 'bold', fontSize: 13 }}>
              Today's Log — {filteredEntries.length} of {entries.length} entries
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 14px', borderBottom: '1px solid var(--border)' }}>
              <input
                type="text"
                placeholder="Search name or Mbr #"
                value={logSearch}
                onChange={e => setLogSearch(e.target.value)}
                style={{ flex: 1, fontSize: 12, padding: '5px 8px' }}
              />
              <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                <input type="checkbox" checked={onsiteOnly} onChange={e => setOnsiteOnly(e.target.checked)} />
                On-Site Only
              </label>
            </div>

            {entries.length === 0 ? (
              <p style={{ padding: 16, color: 'var(--fg3)', fontSize: 13 }}>No sign-ins yet today.</p>
            ) : filteredEntries.length === 0 ? (
              <p style={{ padding: 16, color: 'var(--fg3)', fontSize: 13 }}>
                {onsiteOnly ? 'No members currently on-site match that search.' : 'No sign-ins match that search.'}
              </p>
            ) : (
              <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                {filteredEntries.map(e => (
                  <div key={e.id} onClick={() => openDetail(e)} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
                    padding: '9px 14px', borderBottom: '1px solid var(--border-light)', fontSize: 12,
                    cursor: 'pointer',
                  }}
                  onMouseEnter={ev => ev.currentTarget.style.background = 'var(--bg2)'}
                  onMouseLeave={ev => ev.currentTarget.style.background = ''}
                  >
                    <div>
                      <strong style={{ fontSize: 13 }}>{e.lastname}</strong>
                      <div style={{ color: 'var(--fg3)', marginTop: 1 }}>
                        #{e.membership_number} · {e.member_people}m{e.guest_count > 0 ? `, ${e.guest_count}g` : ''}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      {features.revenue && <div style={{ fontWeight: 'bold' }}>${(e.total_charge || 0).toFixed(2)}</div>}
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end', marginTop: 2 }}>
                        {!e.departure_time && <span className="badge badge-green">on-site</span>}
                        <span className={`badge badge-${e.payment_method === 'card' ? 'blue' : 'green'}`}>{e.payment_method}</span>
                        {e.party_flag ? <span className="badge badge-amber">party</span> : null}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {features.guest_id && (
            <div style={{ marginTop: 16, background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--fg3)' }}>Guest ID Scanning</span>
                <span style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600 }}>{scannedGuests.length} scanned</span>
              </div>
              <div style={{ padding: 12 }}>
                {licenseFlash && (
                  <div style={{
                    padding: '7px 10px', marginBottom: 8, borderRadius: 'var(--radius)', fontSize: 12, fontWeight: 500,
                    background: licenseFlash.type === 'error' ? 'var(--danger-dim)' : 'var(--success-dim)',
                    color: licenseFlash.type === 'error' ? 'var(--danger)' : 'var(--success)',
                  }}>{licenseFlash.msg}</div>
                )}
                <input
                  type="text"
                  placeholder="Scan driver's license barcode..."
                  style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--input-border)', borderRadius: 'var(--radius)', background: 'var(--input-bg)', color: 'var(--fg)', fontSize: 13 }}
                  onChange={e => {
                    const v = e.target.value;
                    clearTimeout(licenseTimer.current);
                    if (v.length > 50) {
                      licenseTimer.current = setTimeout(() => { handleLicense(v); e.target.value = ''; }, 600);
                    }
                  }}
                  onKeyDown={e => {
                    if ((e.key === 'Enter' || e.key === 'Tab') && e.target.value.length > 50) {
                      e.preventDefault();
                      handleLicense(e.target.value);
                      e.target.value = '';
                    }
                  }}
                />
                {scannedGuests.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    {scannedGuests.map((g, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border-light)', fontSize: 12 }}>
                        <span style={{ fontWeight: 600 }}>{g.first_name} {g.last_name}</span>
                        <span style={{ color: 'var(--fg3)' }}>{g.city}, {g.state}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

      </div>

      {detailEntry && (
        <Modal title={`${detailEntry.lastname} — Sign-In Details`} onClose={closeDetail} width={480}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
            {[
              ['Membership #', `#${detailEntry.membership_number}`],
              ['Type',         detailEntry.membership_type || '—'],
              ['Payment',      detailEntry.guest_count > 0 ? detailEntry.payment_method : '—'],
              ['Guard',        detailEntry.guard_name],
              ['Arrival',      detailEntry.created_at ? new Date(detailEntry.created_at.replace(' ', 'T')).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'],
              ['Departure',    detailEntry.departure_time ? new Date(detailEntry.departure_time.replace(' ', 'T')).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'],
              ['Caregiver',    detailEntry.caregiver_present ? 'Yes' : 'No'],
              ['Total Charged', `$${(detailEntry.total_charge || 0).toFixed(2)}`],
            ].map(([label, val]) => (
              <div key={label} style={{ background: 'var(--bg2)', border: '1px solid var(--border-light2)', padding: '8px 10px' }}>
                <div style={{ fontSize: 10, color: 'var(--fg3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{val}</div>
              </div>
            ))}
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Members Today</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="number" min={1} value={detailMembers}
                onChange={e => setDetailMembers(parseInt(e.target.value) || 1)}
                style={{ width: 70, padding: '6px 8px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--fg)', fontSize: 14 }}
              />
              <Btn variant="primary" onClick={saveDetailMembers} disabled={detailSaving} style={{ fontSize: 12, padding: '6px 14px' }}>Save</Btn>
            </div>
          </div>

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Guests</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
              {[
                ['Swimmers',  detailSwimmers,    setDetailSwimmers],
                ['Non-Swim',  detailNonSwimmers, setDetailNonSwimmers],
                ['Under 2',   detailUnder2,      setDetailUnder2],
              ].map(([label, val, setter]) => (
                <div key={label}>
                  <div style={{ fontSize: 10, color: 'var(--fg3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>{label}</div>
                  <input
                    type="number" min={0} value={val}
                    onChange={e => setter(parseInt(e.target.value) || 0)}
                    style={{ width: 70, padding: '6px 8px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--fg)', fontSize: 14 }}
                  />
                </div>
              ))}
              <Btn variant="primary" onClick={saveDetailGuests} disabled={detailSaving} style={{ fontSize: 12, padding: '6px 14px' }}>Save</Btn>
            </div>
            <div style={{ fontSize: 11, color: 'var(--fg3)', marginTop: 5 }}>
              {(detailEntry.guest_count > 0)
                ? `Total: ${detailEntry.guest_count} guest(s) · ${detailEntry.payment_method}`
                : 'No guests'}
            </div>
          </div>

          {features.guest_id && detailEntry.id && (
            <div style={{ marginBottom: 16, paddingTop: 12, borderTop: '1px solid var(--border-light2)' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                Guests Signed In
              </div>
              {detailLicenseFlash && (
                <div style={{
                  padding: '7px 10px', marginBottom: 8, borderRadius: 'var(--radius)', fontSize: 12, fontWeight: 500,
                  background: detailLicenseFlash.type === 'error' ? 'var(--danger-dim)' : 'var(--success-dim)',
                  color: detailLicenseFlash.type === 'error' ? 'var(--danger)' : 'var(--success)',
                }}>{detailLicenseFlash.msg}</div>
              )}
              <input
                type="text"
                placeholder="Scan driver's license barcode..."
                autoComplete="off"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--input-border)', borderRadius: 'var(--radius)', background: 'var(--input-bg)', color: 'var(--fg)', fontSize: 13 }}
                onChange={e => {
                  const v = e.target.value;
                  clearTimeout(detailLicenseTimer.current);
                  if (v.length > 50) {
                    detailLicenseTimer.current = setTimeout(() => { handleDetailLicense(v); e.target.value = ''; }, 600);
                  }
                }}
                onKeyDown={e => {
                  if ((e.key === 'Enter' || e.key === 'Tab') && e.target.value.length > 50) {
                    e.preventDefault();
                    handleDetailLicense(e.target.value);
                    e.target.value = '';
                  }
                }}
              />
              {detailScannedGuests.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  {detailScannedGuests.map((g, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border-light)', fontSize: 12 }}>
                      <span style={{ fontWeight: 600 }}>{g.first_name} {g.last_name}</span>
                      <span style={{ color: 'var(--fg3)' }}>{g.city}, {g.state}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--fg2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Departure</div>
            <Btn
              variant={detailEntry.departure_time ? 'ghost' : 'outline'}
              onClick={saveDetailDeparture}
              disabled={detailSaving}
              style={{ fontSize: 12 }}
            >
              {detailEntry.departure_time ? 'Update Departure' : 'Record Departure'}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
